import React, { useEffect } from 'react';
import { BrowserRouter, Routes, Route, Navigate } from 'react-router-dom';
import { ConfigProvider, Spin, Typography } from 'antd';
import ruRU from 'antd/locale/ru_RU';
import useAuthStore from './store/authStore';
import AppLayout from './components/Layout';
import ContourBanner from './components/ContourBanner';
import UnreceivedModeBanner from './components/UnreceivedModeBanner';
import OperatorGate from './components/auth/OperatorGate';
import Login from './pages/Login';
import EmployeeList from './pages/EmployeeList';
import EmployeeForm from './pages/EmployeeForm';
import AuditLog from './pages/AuditLog';
import AdminUsers from './pages/AdminUsers';
import Settings from './pages/Settings';
import Profile from './pages/Profile';
import BrandsTestPage from './pages/BrandsTestPage';
import Roadmap from './pages/Roadmap';
import WorkplacesPage from './pages/Workplaces';
import EquipmentTypesPage from './pages/EquipmentTypes';
import EquipmentPage from './pages/EquipmentPage';
import ToolsPage from './pages/ToolsPage';
import WarehousePage from './pages/WarehousePage';
import NetworkPage from './pages/NetworkPage';
import CounterpartiesPage from './pages/CounterpartiesPage';
import DocumentScannerPage from './pages/DocumentScannerPage';
import DocumentsListPage from './pages/DocumentsListPage';
import DocumentDetailPage from './pages/DocumentDetailPage';
import CompanyOrdersPage from './pages/CompanyOrdersPage';
import CompanyOrderDetailPage from './pages/CompanyOrderDetailPage';
import CompanyOrderNewPage from './pages/CompanyOrderNewPage';
import IndividualsPage from './pages/IndividualsPage';
import OilOrdersPage from './pages/OilOrdersPage';
import OilOrdersCartPage from './pages/OilOrdersCartPage';
import OilOrdersHistoryPage from './pages/OilOrdersHistoryPage';
import OilPricelistPage from './pages/OilPricelistPage';
import ProductCatalogPage from './pages/ProductCatalogPage';
import ProductTypesPage from './pages/ProductTypesPage';
import MailPage from './pages/MailPage';
import MailSubscriptionsPage from './pages/MailSubscriptionsPage';
import WorkOptimizationPage from './pages/WorkOptimizationPage';
import WorkOptimizationSessionNewPage from './pages/WorkOptimizationSessionNewPage';
import WorkOptimizationTimingPage from './pages/WorkOptimizationTimingPage';
import WorkOptimizationAnalysisPage from './pages/WorkOptimizationAnalysisPage';
import WorkOptimizationOperationsPage from './pages/WorkOptimizationOperationsPage';
import MarkingScanPage from './pages/marking/MarkingScanPage';
import MarkingHistoryPage from './pages/marking/MarkingHistoryPage';
import KKTPage from './pages/kkt/KKTPage';
import MarkingBulkPage from './pages/marking/MarkingBulkPage';
import UnreceivedPage from './pages/UnreceivedPage';
import VehiclesPage from './pages/VehiclesPage';
import ServiceCatalogPage from './pages/ServiceCatalogPage';
import StockPage from './pages/StockPage';
import StockReceiptsPage from './pages/StockReceiptsPage';
import StockReceiptNewPage from './pages/StockReceiptNewPage';
import ServiceOrdersListPage from './pages/ServiceOrdersListPage';
import ServiceOrderPage from './pages/ServiceOrderPage';
import OilChangePage from './pages/OilChangePage';
import QuickSalePage from './pages/QuickSalePage';
import EcologyPage from './pages/EcologyPage';
import WarehouseLiquidsPage from './pages/WarehouseLiquidsPage';
import OneCServiceOrdersPage from './pages/OneCServiceOrdersPage';
import OneCServiceOrderDetailPage from './pages/OneCServiceOrderDetailPage';
import ExpensesPage from './pages/ExpensesPage';
import TimesheetPage from './pages/TimesheetPage';
import MobileMenuPage from './layout/MobileMenuPage';
import useThemeStore from './store/themeStore';
import { antdTheme } from './themes/antd';
import { md3Theme } from './themes/md3';
import './themes/md3.css';

const PrivateRoute: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const { user, loading } = useAuthStore();

  if (loading) {
    return (
      <div style={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '100vh' }}>
        <Spin size="large" />
      </div>
    );
  }

  if (!user) {
    return <Navigate to="/login" replace />;
  }

  return <>{children}</>;
};

/* Порядок проверки для дефолтного редиректа */
const defaultRouteOrder: [string, string][] = [
  ['employees:read', '/employees'],
  ['oil:read', '/oil-orders'],
  ['documents:read', '/documents'],
  ['counterparties:read', '/counterparties'],
  ['individuals:read', '/individuals'],
  ['departments:write', '/settings'],
  ['users:read', '/users'],
  ['audit:read', '/audit'],
  ['marking:read', '/marking/history'],
  ['mail:read', '/mail'],
  ['orders:read', '/service-orders'],
  ['ecology:read', '/ecology'],
  ['accounting:read', '/expenses'],
];

// Per-role override for default landing page (takes precedence over defaultRouteOrder)
const roleStartPage: Record<string, string> = {
  oil_manager: '/marking/scan',
  cashier: '/marking/scan',
};

const roleStartPagePerm: Record<string, string> = {
  oil_manager: 'marking:write',
  cashier: 'marking:write',
};

const DefaultRedirect: React.FC = () => {
  const { user, hasPermission } = useAuthStore();
  if (user?.role && roleStartPage[user.role]) {
    const requiredPerm = roleStartPagePerm[user.role];
    if (!requiredPerm || hasPermission(requiredPerm)) {
      return <Navigate to={roleStartPage[user.role]} replace />;
    }
  }
  for (const [perm, path] of defaultRouteOrder) {
    if (hasPermission(perm)) return <Navigate to={path} replace />;
  }
  if (user) {
    return (
      <div style={{ padding: 40, textAlign: 'center' }}>
        <Typography.Title level={3}>Нет доступных разделов</Typography.Title>
        <Typography.Text>Обратитесь к администратору</Typography.Text>
      </div>
    );
  }
  return <Navigate to="/login" replace />;
};

const RequirePermission: React.FC<{ perm: string; children: React.ReactNode }> = ({ perm, children }) => {
  const { user, hasPermission } = useAuthStore();
  if (hasPermission(perm)) return <>{children}</>;
  if (user) {
    return (
      <div style={{ padding: 40, textAlign: 'center' }}>
        <Typography.Title level={3}>Доступ запрещён</Typography.Title>
        <Typography.Text>У вас нет права {perm}</Typography.Text>
      </div>
    );
  }
  return <Navigate to="/login" replace />;
};

const App: React.FC = () => {
  const { fetchMe } = useAuthStore();
  const themeMode = useThemeStore((s) => s.mode);

  useEffect(() => {
    const token = localStorage.getItem('access_token');
    if (token) {
      fetchMe();
    } else {
      useAuthStore.setState({ loading: false });
    }
  }, [fetchMe]);

  return (
    <ConfigProvider locale={ruRU} theme={themeMode === 'md3' ? md3Theme : antdTheme}>
      <BrowserRouter>
        <ContourBanner />
        <UnreceivedModeBanner />
        <OperatorGate />
        <Routes>
          <Route path="/login" element={<Login />} />
          <Route
            path="/"
            element={
              <PrivateRoute>
                <AppLayout />
              </PrivateRoute>
            }
          >
            <Route index element={<DefaultRedirect />} />
            <Route path="employees" element={<RequirePermission perm="employees:read"><EmployeeList /></RequirePermission>} />
            <Route path="employees/new" element={<RequirePermission perm="employees:read"><EmployeeForm /></RequirePermission>} />
            <Route path="employees/:id/edit" element={<RequirePermission perm="employees:read"><EmployeeForm /></RequirePermission>} />
            <Route path="timesheet" element={<RequirePermission perm="employees:read"><TimesheetPage /></RequirePermission>} />
            <Route path="settings" element={<RequirePermission perm="departments:write"><Settings /></RequirePermission>} />
            <Route path="profile" element={<Profile />} />
            <Route path="brands-test" element={<BrandsTestPage />} />
            <Route path="audit" element={<RequirePermission perm="audit:read"><AuditLog /></RequirePermission>} />
            <Route path="users" element={<RequirePermission perm="users:read"><AdminUsers /></RequirePermission>} />
            <Route path="roadmap" element={<RequirePermission perm="users:read"><Roadmap /></RequirePermission>} />
            <Route path="workplaces" element={<RequirePermission perm="departments:write"><WorkplacesPage /></RequirePermission>} />
            <Route path="equipment-types" element={<RequirePermission perm="departments:write"><EquipmentTypesPage /></RequirePermission>} />
            <Route path="equipment" element={<RequirePermission perm="departments:write"><EquipmentPage /></RequirePermission>} />
            <Route path="tools" element={<RequirePermission perm="departments:write"><ToolsPage /></RequirePermission>} />
            <Route path="warehouse" element={<RequirePermission perm="departments:write"><WarehousePage /></RequirePermission>} />
            <Route path="network" element={<RequirePermission perm="departments:write"><NetworkPage /></RequirePermission>} />
            <Route path="counterparties" element={<RequirePermission perm="counterparties:read"><CounterpartiesPage /></RequirePermission>} />
            <Route path="document-scanner" element={<RequirePermission perm="counterparties:read"><DocumentScannerPage /></RequirePermission>} />
            <Route path="documents" element={<RequirePermission perm="documents:read"><DocumentsListPage /></RequirePermission>} />
            <Route path="documents/scan" element={<RequirePermission perm="documents:write"><DocumentScannerPage /></RequirePermission>} />
            <Route path="documents/:id" element={<RequirePermission perm="documents:read"><DocumentDetailPage /></RequirePermission>} />
            <Route path="company-orders" element={<RequirePermission perm="documents:read"><CompanyOrdersPage /></RequirePermission>} />
            <Route path="company-orders/new" element={<RequirePermission perm="documents:write"><CompanyOrderNewPage /></RequirePermission>} />
            <Route path="company-orders/:id" element={<RequirePermission perm="documents:read"><CompanyOrderDetailPage /></RequirePermission>} />
            <Route path="individuals" element={<RequirePermission perm="individuals:read"><IndividualsPage /></RequirePermission>} />
            <Route path="oil-orders" element={<RequirePermission perm="oil:read"><OilOrdersPage /></RequirePermission>} />
            <Route path="oil-orders/cart" element={<RequirePermission perm="oil:read"><OilOrdersCartPage /></RequirePermission>} />
            <Route path="oil-orders/history" element={<RequirePermission perm="oil:read"><OilOrdersHistoryPage /></RequirePermission>} />
            <Route path="oil-pricelists" element={<RequirePermission perm="oil:read"><OilPricelistPage /></RequirePermission>} />
            <Route path="product-catalog" element={<RequirePermission perm="oil:read"><ProductCatalogPage /></RequirePermission>} />
            <Route path="product-types" element={<RequirePermission perm="oil:read"><ProductTypesPage /></RequirePermission>} />
            <Route path="mail" element={<RequirePermission perm="mail:read"><MailPage /></RequirePermission>} />
            <Route path="mail/subscriptions" element={<RequirePermission perm="mail:read"><MailSubscriptionsPage /></RequirePermission>} />
            <Route path="work-optimization" element={<RequirePermission perm="departments:write"><WorkOptimizationPage /></RequirePermission>} />
            <Route path="work-optimization/session/new" element={<RequirePermission perm="departments:write"><WorkOptimizationSessionNewPage /></RequirePermission>} />
            <Route path="work-optimization/session/:id/timing" element={<RequirePermission perm="departments:write"><WorkOptimizationTimingPage /></RequirePermission>} />
            <Route path="work-optimization/session/:id/analysis" element={<RequirePermission perm="departments:write"><WorkOptimizationAnalysisPage /></RequirePermission>} />
            <Route path="work-optimization/operations" element={<RequirePermission perm="departments:write"><WorkOptimizationOperationsPage /></RequirePermission>} />
            <Route path="marking/scan" element={<RequirePermission perm="marking:write"><MarkingScanPage /></RequirePermission>} />
            <Route path="marking/bulk" element={<RequirePermission perm="marking:read"><MarkingBulkPage /></RequirePermission>} />
            <Route path="marking/history" element={<RequirePermission perm="marking:read"><MarkingHistoryPage /></RequirePermission>} />
            <Route path="unreceived" element={<RequirePermission perm="marking:read"><UnreceivedPage /></RequirePermission>} />
            <Route path="kkt" element={<RequirePermission perm="direct_posting:use"><KKTPage /></RequirePermission>} />
            <Route path="vehicles" element={<RequirePermission perm="orders:read"><VehiclesPage /></RequirePermission>} />
            <Route path="service-catalog" element={<RequirePermission perm="orders:read"><ServiceCatalogPage /></RequirePermission>} />
            <Route path="stock" element={<RequirePermission perm="orders:read"><StockPage /></RequirePermission>} />
            <Route path="stock/receipts" element={<RequirePermission perm="orders:read"><StockReceiptsPage /></RequirePermission>} />
            <Route path="stock/receipt/new" element={<RequirePermission perm="orders:write"><StockReceiptNewPage /></RequirePermission>} />
            <Route path="service-orders" element={<RequirePermission perm="orders:read"><ServiceOrdersListPage /></RequirePermission>} />
            <Route path="service-orders/new" element={<RequirePermission perm="orders:write"><ServiceOrderPage /></RequirePermission>} />
            <Route path="service-orders/:id" element={<RequirePermission perm="orders:read"><ServiceOrderPage /></RequirePermission>} />
            {/* Замена масла: форма + колонка очереди на одной странице (OilChangePage.md §3 rev. 2026-09-08) */}
            <Route path="oil-change" element={<RequirePermission perm="orders:read"><OilChangePage /></RequirePermission>} />
            <Route path="oil-change/new" element={<Navigate to="/oil-change" replace />} />
            <Route path="oil-change/:id" element={<RequirePermission perm="orders:read"><OilChangePage /></RequirePermission>} />
            <Route path="quick-sale" element={<RequirePermission perm="orders:write"><QuickSalePage /></RequirePermission>} />
            <Route path="quick-sale/:id" element={<RequirePermission perm="orders:read"><QuickSalePage /></RequirePermission>} />
            <Route path="1c-stock" element={<Navigate to="/warehouse-liquids" replace />} />
            <Route path="warehouse-liquids" element={<RequirePermission perm="orders:read"><WarehouseLiquidsPage /></RequirePermission>} />
            <Route path="1c-service-orders" element={<RequirePermission perm="orders:read"><OneCServiceOrdersPage /></RequirePermission>} />
            <Route path="1c-service-orders/:id" element={<RequirePermission perm="orders:read"><OneCServiceOrderDetailPage /></RequirePermission>} />
            <Route path="ecology" element={<RequirePermission perm="ecology:read"><EcologyPage /></RequirePermission>} />
            <Route path="expenses" element={<RequirePermission perm="accounting:read"><ExpensesPage /></RequirePermission>} />
            <Route path="menu" element={<MobileMenuPage />} />
          </Route>
        </Routes>
      </BrowserRouter>
    </ConfigProvider>
  );
};

export default App;
